import useGetCustomData from "../Hooks/useGetCustomData";
import { USER_ROLE } from "../Types";
import { useEffect, useMemo, useRef, useState } from "react";
import { WidgetExportMenu } from "../Components/Home/WidgetExportMenu";
import { LocationSelect } from "../Components/Select/LocationSelect";
import { decode } from "../Services/DecodeToken";
import { PageContainer } from "../Styles/AppStyles";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Divider,
  useTheme,
  Paper,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { DragIndicator, Close, Category, People, Place, Assignment } from "@mui/icons-material";
import { COLORS } from "../Styles/Colors";
import { useWidgetPreferences } from "../Context/WidgetPreferencesContext";
import { useSnackbar } from "../Context/SnackbarProvider";
import { DateRangePicker } from "../Components/Date/Datepicker";
import { useCompactLayout } from "../Hooks/useCompactLayout";

interface UsageEntry {
  name: string;
  total: number;
  date?: string;
  id?: number; // optional identifier (e.g., category id)
  part_number?: string; // item drill-downs: product part number
  description?: string; // item drill-downs: product description
}

interface UsageAnalytics {
  productCategory: UsageEntry[];
  productCategoryVolume: UsageEntry[];
  user: UsageEntry[];
  employeeVolume: UsageEntry[];
  requesterVolume: UsageEntry[];
  requesterCost: UsageEntry[];
  location: UsageEntry[];
  locationVolume: UsageEntry[];
  poJobCost: UsageEntry[];
  topProduct?: UsageEntry;
  topProductVolume?: UsageEntry;
  topUser?: UsageEntry;
  topUserVolume?: UsageEntry;
}

interface Widget {
  id: string;
  title: string;
  chartData: UsageEntry[];
  color: string;
  type: 'productCategory' | 'productCategoryVolume' | 'user' | 'employeeVolume' | 'requesterVolume' | 'requesterCost' | 'location' | 'locationVolume' | 'poJobCost';
}

interface DropZone {
  id: string;
  title: string;
  widgets: Widget[];
}

const highlightCardSx = {
  p: { xs: 0, md: 0 },
  minWidth: 0,
};

const highlightOverlineSx = {
  color: `${COLORS.white}d9`,
  fontSize: { xs: '0.65rem', md: '0.75rem' },
  letterSpacing: '0.04em',
  lineHeight: 1.3,
  display: 'block',
  minHeight: { md: '2.4em' },
};

const highlightNameSx = {
  fontWeight: 600,
  fontSize: { xs: '0.875rem', md: '1.25rem' },
  color: COLORS.white,
  lineHeight: 1.3,
};

const highlightValueSx = {
  fontWeight: 700,
  fontSize: { xs: '1rem', md: '1.5rem' },
  color: COLORS.white,
  lineHeight: 1.3,
};

const highlightSkeletonSx = {
  bgcolor: `${COLORS.white}4d`,
};

const HighlightStat = ({
  label,
  name,
  value,
  loading,
  show,
}: {
  label: string;
  name?: string;
  value?: string;
  loading: boolean;
  show: boolean;
}) => {
  if (!show) return null;
  return (
    <Box sx={highlightCardSx}>
      <Typography variant="overline" sx={highlightOverlineSx}>
        {label}
      </Typography>
      {loading ? (
        <>
          <Skeleton variant="text" width="80%" height={24} sx={highlightSkeletonSx} />
          <Skeleton variant="text" width="50%" height={28} sx={highlightSkeletonSx} />
        </>
      ) : (
        <>
          <Typography noWrap title={name} sx={highlightNameSx}>
            {name || '—'}
          </Typography>
          <Typography sx={highlightValueSx}>{value || '—'}</Typography>
        </>
      )}
    </Box>
  );
};

export const TransactionUsage = () => {
  const isCompact = useCompactLayout();
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  // Default to empty string for "All Locations"
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [draggedWidget, setDraggedWidget] = useState<Widget | null>(null);
  const token: any = decode();
  const theme = useTheme();

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState<Date | null>(firstOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(today);

  const formatDate = (date: Date) => {
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split("T")[0];
  };

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
    // Only add ovid param if a specific location is selected (not empty string)
    if (selectedOvs && selectedOvs !== '') {
      params.set('ovid', selectedOvs);
    }
    if (startDate) {
      const formatted = formatDate(startDate);
      if (formatted) params.set('startDate', formatted);
    }
    if (endDate) {
      const formatted = formatDate(endDate);
      if (formatted) params.set('endDate', formatted);
    }
    return `/api/transactions/analytics?${params.toString()}`;
  }, [selectedOvs, startDate, endDate]);

  const [data, loading] = useGetCustomData(baseUrl);

  // Drill-down modal state for product category -> items
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [selectedCategoryMode, setSelectedCategoryMode] = useState<'cost' | 'volume'>('cost');

  const categoryItemsUrl = useMemo(() => {
    if (!selectedCategoryId) return '';
    const params = new URLSearchParams();
    if (selectedOvs && selectedOvs !== '') {
      params.set('ovid', selectedOvs);
    }
    if (startDate) {
      const formatted = formatDate(startDate);
      if (formatted) params.set('startDate', formatted);
    }
    if (endDate) {
      const formatted = formatDate(endDate);
      if (formatted) params.set('endDate', formatted);
    }
    params.set('catid', String(selectedCategoryId));
    if (selectedCategoryMode === 'volume') {
      params.set('mode', 'volume');
    }
    return `/api/transactions/analytics/category-items?${params.toString()}`;
  }, [selectedCategoryId, selectedOvs, startDate, endDate, selectedCategoryMode]);

  const [categoryItems, categoryItemsLoading] = useGetCustomData(
    categoryItemsUrl || '',
  );

  // Drill-down modal state for user -> products
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
  const [selectedUserTotal, setSelectedUserTotal] = useState<number | null>(null);
  const [showAllProducts, setShowAllProducts] = useState<boolean>(false);
  const [fullDataWidget, setFullDataWidget] = useState<Widget | null>(null);

  const isCostType = (type: Widget['type']) =>
    (['productCategory', 'location', 'user', 'requesterCost', 'poJobCost'] as Widget['type'][]).includes(type);

  const formatYAxis = (value: number, type: Widget['type']): string => {
    const prefix = isCostType(type) ? '$' : '';
    if (value >= 1000000) {
      const m = value / 1000000;
      return `${prefix}${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
    }
    if (value >= 1000) {
      const k = value / 1000;
      return `${prefix}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
    }
    return `${prefix}${value}`;
  };

  // MySQL SUM/DECIMAL values often arrive as strings; coerce so commas apply.
  const formatHighlightCurrency = (value: number | string) =>
    `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatHighlightNumber = (value: number | string) =>
    Number(value).toLocaleString('en-US');

  const userProductsUrl = useMemo(() => {
    if (!selectedUserId) return '';
    const params = new URLSearchParams();
    if (selectedOvs && selectedOvs !== '') {
      params.set('ovid', selectedOvs);
    }
    if (startDate) {
      const formatted = formatDate(startDate);
      if (formatted) params.set('startDate', formatted);
    }
    if (endDate) {
      const formatted = formatDate(endDate);
      if (formatted) params.set('endDate', formatted);
    }
    params.set('userid', String(selectedUserId));
    return `/api/transactions/analytics/user-products?${params.toString()}`;
  }, [selectedUserId, selectedOvs, startDate, endDate]);

  const [userProducts, userProductsLoading] = useGetCustomData(
    userProductsUrl || '',
  );

  const { visibleWidgets, updateVisibleWidgets, setWidgetVisible, ready } = useWidgetPreferences();
  const { showSnackbar } = useSnackbar();

  const allWidgets: Widget[] = [
    {
      id: 'productCategory',
      title: 'Usage by Product Category (Cost)',
      chartData: loading ? [] : data?.productCategory || [],
      color: COLORS.teal,
      type: 'productCategory'
    },
    {
      id: 'productCategoryVolume',
      title: 'Usage by Product Category (Volume)',
      chartData: loading ? [] : data?.productCategoryVolume || [],
      color: COLORS.teal,
      type: 'productCategoryVolume'
    },
    {
      id: 'location',
      title: 'Usage by Location (Cost)',
      chartData: loading ? [] : data?.location || [],
      color: COLORS.teal,
      type: 'location'
    },
    {
      id: 'locationVolume',
      title: 'Usage by Location (Volume)',
      chartData: loading ? [] : data?.locationVolume || [],
      color: COLORS.teal,
      type: 'locationVolume'
    },
    {
      id: 'user',
      title: 'Usage by Employee (Cost)',
      chartData: loading ? [] : data?.user || [],
      color: COLORS.teal,
      type: 'user'
    },
    {
      id: 'employeeVolume',
      title: 'Usage by Employee (Volume)',
      chartData: loading ? [] : data?.employeeVolume || [],
      color: COLORS.teal,
      type: 'employeeVolume'
    },
    {
      id: 'requesterCost',
      title: 'Usage by Requester (Cost)',
      chartData: loading ? [] : data?.requesterCost || [],
      color: COLORS.teal,
      type: 'requesterCost'
    },
    {
      id: 'requesterVolume',
      title: 'Usage by Requester (Volume)',
      chartData: loading ? [] : data?.requesterVolume || [],
      color: COLORS.teal,
      type: 'requesterVolume'
    },
    {
      id: 'poJobCost',
      title: 'Usage by PO/Job No. (Cost)',
      chartData: loading ? [] : data?.poJobCost || [],
      color: COLORS.teal,
      type: 'poJobCost'
    },
  ];

  const availableWidgets = useMemo(() => {
    const byId = new Map(allWidgets.map((widget) => [widget.id, widget]));
    return visibleWidgets
      .map((id) => byId.get(id))
      .filter((widget): widget is Widget => Boolean(widget));
  }, [data, loading, visibleWidgets]);

  const [dropZones, setDropZones] = useState<DropZone[]>([]);

  useEffect(() => {
    const zoneCount = Math.max(availableWidgets.length, 2);
    const newZones: DropZone[] = Array.from({ length: zoneCount }, (_, index) => ({
      id: `section${index + 1}`,
      title: `Section ${index + 1}`,
      widgets: [] as Widget[],
    }));

    availableWidgets.forEach((widget, index) => {
      if (index < newZones.length) {
        newZones[index].widgets.push(widget);
      }
    });

    setDropZones(newZones);
  }, [availableWidgets]);


  const handleDragStart = (e: React.DragEvent, widget: Widget) => {
    setDraggedWidget(widget);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropZoneId: string) => {
    e.preventDefault();

    if (!draggedWidget) return;

    const targetZoneIndex = dropZones.findIndex((z) => z.id === dropZoneId);
    const sourceZoneIndex = dropZones.findIndex((z) =>
      z.widgets.some((w) => w.id === draggedWidget.id),
    );

    if (targetZoneIndex === -1 || sourceZoneIndex === -1 || sourceZoneIndex === targetZoneIndex) {
      setDraggedWidget(null);
      return;
    }

    const newZones = dropZones.map((zone) => ({
      ...zone,
      widgets: [...zone.widgets],
    }));

    const targetZone = newZones[targetZoneIndex];
    newZones[sourceZoneIndex] = {
      ...newZones[sourceZoneIndex],
      widgets: newZones[sourceZoneIndex].widgets.filter((w) => w.id !== draggedWidget.id),
    };

    if (targetZone.widgets.length > 0) {
      const existingWidget = targetZone.widgets[0];
      newZones[sourceZoneIndex] = {
        ...newZones[sourceZoneIndex],
        widgets: [existingWidget],
      };
      newZones[targetZoneIndex] = {
        ...newZones[targetZoneIndex],
        widgets: [draggedWidget],
      };
    } else {
      newZones[targetZoneIndex] = {
        ...newZones[targetZoneIndex],
        widgets: [draggedWidget],
      };
    }

    const nextVisibleOrder = newZones.flatMap((zone) => zone.widgets.map((w) => w.id));
    updateVisibleWidgets(nextVisibleOrder);
    showSnackbar("Widget layout saved", "success");
    setDraggedWidget(null);
  };

  const removeWidgetFromZone = (_zoneId: string, widgetId: string) => {
    if (visibleWidgets.length <= 1) {
      showSnackbar("At least one widget must remain visible", "warning");
      return;
    }
    setWidgetVisible(widgetId, false);
    showSnackbar("Widget layout saved", "success");
  };

  // Filter context stamped onto every widget export.
  const exportFilters = (): Record<string, string> => {
    const location = selectedOvs
      ? (ovs as { OVID: string | number; name: string }[] | undefined)?.find(
          (o) => String(o.OVID) === String(selectedOvs),
        )?.name ?? `Location ${selectedOvs}`
      : 'All Locations';
    const start = startDate ? formatDate(startDate) : null;
    const end = endDate ? formatDate(endDate) : null;
    return {
      Location: location,
      'Date Range': `${start ?? 'Any'} to ${end ?? 'Any'}`,
    };
  };

  // Ensure every item row has a part_number for the axis (fall back to name).
  const withPartNumber = (rows: UsageEntry[]): UsageEntry[] =>
    rows.map((row) => ({ ...row, part_number: row.part_number || row.name }));

  // Tooltip for item drill-downs: part number, description, and value stacked.
  const ItemTooltip = ({
    active,
    payload,
    valueLabel,
    formatValue,
  }: {
    active?: boolean;
    payload?: any[];
    valueLabel: string;
    formatValue: (value: number) => string;
  }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload as UsageEntry;
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          boxShadow: theme.shadows[3],
          px: 1.5,
          py: 1,
          maxWidth: 280,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: COLORS.teal }}>
          {row.part_number || row.name || '—'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
          {row.description || row.name || '—'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
          {`${valueLabel}: ${formatValue(Number(row.total))}`}
        </Typography>
      </Box>
    );
  };

  const ChartCard = ({
    widget,
    isInDropZone = false,
    zoneId,
    showRemoveButton = false,
  }: {
    widget: Widget;
    isInDropZone?: boolean;
    zoneId?: string;
    showRemoveButton?: boolean;
  }) => {

    const iconMap = {
      productCategory: <Category fontSize="small" sx={{ color: COLORS.teal }} />,
      productCategoryVolume: <Category fontSize="small" sx={{ color: COLORS.teal }} />,
      user: <People fontSize="small" sx={{ color: COLORS.teal }} />,
      employeeVolume: <People fontSize="small" sx={{ color: COLORS.teal }} />,
      requesterVolume: <People fontSize="small" sx={{ color: COLORS.teal }} />,
      requesterCost: <People fontSize="small" sx={{ color: COLORS.teal }} />,
      location: <Place fontSize="small" sx={{ color: COLORS.teal }} />,
      locationVolume: <Place fontSize="small" sx={{ color: COLORS.teal }} />,
      poJobCost: <Assignment fontSize="small" sx={{ color: COLORS.teal }} />,
    };

    const sortedData = [...widget.chartData].sort((a, b) => Number(b.total) - Number(a.total));
    const displayData = sortedData.slice(0, 10);
    const graphTotal = widget.chartData.reduce((sum, entry) => sum + Number(entry.total || 0), 0);
    const chartRef = useRef<HTMLDivElement>(null);

    // Export covers the full dataset (the chart only shows the top 10).
    const getExportOptions = () => ({
      title: widget.title,
      rows: widget.chartData,
      isCost: isCostType(widget.type),
      filters: exportFilters(),
      chartSvg: chartRef.current?.querySelector<SVGSVGElement>('svg.recharts-surface') ?? null,
    });

    return (
      <Card
        draggable
        onDragStart={(e) => handleDragStart(e, widget)}
        sx={{
          width: "100%",
          maxWidth: isInDropZone ? "100%" : 900,
          margin: "0 auto",
          borderRadius: 3,
          boxShadow: theme.shadows[6],
          backgroundColor: theme.palette.background.paper,
          cursor: 'move',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[8],
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <CardContent sx={{ px: 3, py: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <DragIndicator fontSize="small" sx={{ color: 'text.secondary' }} />
              {iconMap[widget.type]}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {widget.title}
              </Typography>
              {isCostType(widget.type) && !loading && (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: COLORS.teal }}
                >
                  {formatHighlightCurrency(graphTotal)}
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <WidgetExportMenu
                getExportOptions={getExportOptions}
                disabled={loading || widget.chartData.length === 0}
                onError={(message) => showSnackbar(`Export failed: ${message}`, "error")}
              />
              {showRemoveButton && zoneId && (
                <IconButton
                  size="small"
                  onClick={() => removeWidgetFromZone(zoneId, widget.id)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 220,
              }}
            >
              <CircularProgress />
            </Box>
          ) : displayData.length ? (
            <Box ref={chartRef}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={displayData}
                margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                onClick={(chartData) => {
                  if (widget.type === 'productCategory' || widget.type === 'productCategoryVolume') {
                    if (!chartData || !chartData.activeLabel) return;
                    const activeIndex = chartData?.activeTooltipIndex;
                    if (activeIndex === undefined || activeIndex === null) return;
                    const numericIndex = typeof activeIndex === 'number' ? activeIndex : Number(activeIndex);
                    if (Number.isNaN(numericIndex) || !displayData[numericIndex]) return;
                    const entry = displayData[numericIndex] as UsageEntry;
                    if (entry.id) {
                      setSelectedCategoryId(entry.id);
                      setSelectedCategoryName(entry.name);
                      setSelectedCategoryMode(widget.type === 'productCategoryVolume' ? 'volume' : 'cost');
                    }
                  } else if (widget.type === 'user') {
                    if (!chartData || !chartData.activeLabel) return;
                    const activeIndex = chartData?.activeTooltipIndex;
                    if (activeIndex === undefined || activeIndex === null) return;
                    const numericIndex = typeof activeIndex === 'number' ? activeIndex : Number(activeIndex);
                    if (Number.isNaN(numericIndex) || !displayData[numericIndex]) return;
                    const entry = displayData[numericIndex] as UsageEntry;
                    if (entry.id) {
                      setSelectedUserId(entry.id);
                      setSelectedUserName(entry.name);
                      setSelectedUserTotal(entry.total);
                    }
                  } else {
                    setFullDataWidget(widget);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value.length > 12 ? `${value.slice(0, 12)}...` : value}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  width={60}
                  tickFormatter={(value) => formatYAxis(value, widget.type)}
                  label={
                    isCostType(widget.type)
                      ? { value: 'Cost ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
                      : { value: 'Quantity', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
                  }
                />
                <Tooltip
                  formatter={(value: number) => {
                    if (widget.type === 'productCategory') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Category Total (Cost)'];
                    if (widget.type === 'productCategoryVolume') return [value.toLocaleString(), 'Category Total (Qty)'];
                    if (widget.type === 'employeeVolume') return [value.toLocaleString(), 'Employee Total (Qty)'];
                    if (widget.type === 'requesterVolume') return [value.toLocaleString(), 'Requester Total (Qty)'];
                    if (widget.type === 'requesterCost') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Requester Total (Cost)'];
                    if (widget.type === 'user') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Employee Total (Cost)'];
                    if (widget.type === 'location') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Location Total (Cost)'];
                    if (widget.type === 'locationVolume') return [value.toLocaleString(), 'Location Total (Qty)'];
                    if (widget.type === 'poJobCost') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'PO/Job Total (Cost)'];
                    return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Total'];
                  }}
                />
                <Bar
                  dataKey="total"
                  fill={widget.color}
                  radius={[4, 4, 0, 0]}
                  animationDuration={500}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            >
              No data available for this selection.
            </Typography>
          )}
        </CardContent>
      </Card>
    )
  }

  const DropZoneCard = ({ zone }: { zone: DropZone }) => (
    <Box
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, zone.id)}
      sx={{
        minHeight: 400,
        backgroundColor: theme.palette.background.default,
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {zone.widgets.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.text.secondary,
            border: draggedWidget ? `2px dashed ${theme.palette.divider}` : '2px solid transparent',
            borderRadius: 1,
            minHeight: 300,
            transition: 'all 0.2s ease-in-out',
            '&:hover': draggedWidget ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            } : {},
          }}
        >
          <Typography variant="body2" sx={{ 
            opacity: draggedWidget ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out'
          }}>
            Drop widget here
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1 }}>
          {zone.widgets.map((widget) => (
            <ChartCard
              key={widget.id}
              widget={widget}
              isInDropZone={true}
              zoneId={zone.id}
              showRemoveButton={true}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  if (!ready) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Full data modal for non-drill-down charts */}
      <Dialog
        open={!!fullDataWidget}
        onClose={() => setFullDataWidget(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isCompact}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {fullDataWidget?.title} — All Results
            </Typography>
            <IconButton size="small" onClick={() => setFullDataWidget(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {fullDataWidget?.chartData.length ? (
            <Box sx={{ width: '100%', height: Math.min(640, Math.max(320, fullDataWidget.chartData.length * 32)) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={[...fullDataWidget.chartData].sort((a, b) => Number(b.total) - Number(a.total))}
                  margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(value: number) => formatYAxis(value, fullDataWidget.type)}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: string) => value.length > 22 ? `${value.slice(0, 22)}...` : value}
                  />
                  <Tooltip
                    formatter={(value: number) => {
                      if (fullDataWidget.type === 'location') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Location Total (Cost)'];
                      if (fullDataWidget.type === 'locationVolume') return [value.toLocaleString(), 'Location Total (Qty)'];
                      if (fullDataWidget.type === 'employeeVolume') return [value.toLocaleString(), 'Employee Total (Qty)'];
                      if (fullDataWidget.type === 'requesterCost') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Requester Total (Cost)'];
                      if (fullDataWidget.type === 'requesterVolume') return [value.toLocaleString(), 'Requester Total (Qty)'];
                      if (fullDataWidget.type === 'poJobCost') return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'PO/Job Total (Cost)'];
                      return [value.toLocaleString(), 'Total'];
                    }}
                  />
                  <Bar dataKey="total" fill={COLORS.teal} radius={[0, 4, 4, 0]} animationDuration={400} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              No data available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Category items drill-down modal */}
      <Dialog
        open={!!selectedCategoryId}
        onClose={() => {
          setSelectedCategoryId(null);
          setSelectedCategoryName(null);
          setSelectedCategoryMode('cost');
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isCompact}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="h6">
                {selectedCategoryName
                  ? `Items in ${selectedCategoryName}`
                  : 'Category Items'}
              </Typography>
              {startDate && endDate && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  {`Date Range: ${startDate ? (formatDate(startDate) ?? '') : ''} - ${endDate ? (formatDate(endDate) ?? '') : ''}`}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedCategoryId(null);
                setSelectedCategoryName(null);
                setSelectedCategoryMode('cost');
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {categoryItemsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 260,
              }}
            >
              <CircularProgress />
            </Box>
          ) : categoryItems && categoryItems.length > 0 ? (
            <Box sx={{ width: '100%', height: Math.min(500, Math.max(280, categoryItems.length * 32)) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={withPartNumber([...categoryItems]).sort((a: UsageEntry, b: UsageEntry) => b.total - a.total)} margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(value: number) =>
                      selectedCategoryMode === 'cost'
                        ? formatYAxis(value, 'productCategory')
                        : formatYAxis(value, 'productCategoryVolume')
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey="part_number"
                    width={140}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: string) => value && value.length > 22 ? `${value.slice(0, 22)}...` : value}
                  />
                  <Tooltip
                    content={
                      <ItemTooltip
                        valueLabel={selectedCategoryMode === 'cost' ? 'Item Total (Cost)' : 'Item Total (Qty)'}
                        formatValue={(value: number) =>
                          selectedCategoryMode === 'cost'
                            ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                            : value.toLocaleString()
                        }
                      />
                    }
                  />
                  <Bar
                    dataKey="total"
                    fill={COLORS.teal}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            >
              No item data available for this category.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* User products drill-down modal */}
      <Dialog
        open={!!selectedUserId}
        onClose={() => {
          setSelectedUserId(null);
          setSelectedUserName(null);
          setSelectedUserTotal(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isCompact}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="h6">
                {selectedUserName
                  ? `Products used by ${selectedUserName}`
                  : 'Employee Products'}
              </Typography>
              {selectedUserTotal !== null && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: COLORS.teal,
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  Total: ${selectedUserTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedUserId(null);
                setSelectedUserName(null);
                setSelectedUserTotal(null);
                setShowAllProducts(false);
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {userProductsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 260,
              }}
            >
              <CircularProgress />
            </Box>
          ) : userProducts && userProducts.length > 0 ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: 'rgba(0, 152, 170, 0.05)',
                  borderRadius: 1,
                  border: `1px solid ${COLORS.teal}20`,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Breakdown of ${selectedUserTotal?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || '0.00'}:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {(showAllProducts
                    ? userProducts
                    : (userProducts as UsageEntry[]).slice(0, 10)
                  ).map((product: UsageEntry, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        border: `1px solid ${COLORS.teal}30`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {product.name.length > 30
                          ? `${product.name.slice(0, 30)}...`
                          : product.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: COLORS.teal, fontWeight: 600 }}
                      >
                        ${product.total.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {(userProducts as UsageEntry[]).length > 10 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      onClick={() => setShowAllProducts(!showAllProducts)}
                      sx={{
                        color: COLORS.teal,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: `${COLORS.teal}10`,
                        },
                      }}
                    >
                      {showAllProducts
                        ? 'See Less'
                        : `See More (${(userProducts as UsageEntry[]).length - 10} more products)`}
                    </Button>
                  </Box>
                )}
             
              </Box>
              <Box sx={{ width: '100%', height: Math.min(500, Math.max(280, (userProducts as UsageEntry[]).length * 32)) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={withPartNumber([...(userProducts as UsageEntry[])]).sort((a, b) => b.total - a.total)} margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value: number) => formatYAxis(value, 'user')}
                    />
                    <YAxis
                      type="category"
                      dataKey="part_number"
                      width={140}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value: string) => value && value.length > 22 ? `${value.slice(0, 22)}...` : value}
                    />
                    <Tooltip
                      content={
                        <ItemTooltip
                          valueLabel="Product Total (Cost)"
                          formatValue={(value: number) =>
                            `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          }
                        />
                      }
                    />
                    <Bar
                      dataKey="total"
                      fill={COLORS.teal}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            >
              No product data available for this employee.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 1.5, md: 4 },
              borderRadius: { xs: 2, md: 3 },
              backgroundColor: COLORS.teal,
              color: COLORS.white,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: { xs: 1, md: 2 },
                mb: { xs: 1.25, md: 3 },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: COLORS.white,
                  fontSize: { xs: '1.05rem', md: '1.5rem' },
                }}
              >
                Highlights
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, md: 2 },
                  alignItems: { xs: 'stretch', sm: 'flex-end' },
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' },
                  minWidth: 0,
                }}
              >
                <LocationSelect
                  ovs={ovs}
                  selectedOvs={selectedOvs}
                  onChange={setSelectedOvs}
                  setDefault={true}
                  defaultName="All Locations"
                  sx={{
                    backgroundColor: COLORS.white,
                    minWidth: { xs: 0, sm: 200 },
                    width: { xs: '100%', sm: 'auto' },
                    borderRadius: 1,
                  }}
                />

                <Box sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0, flex: { sm: 1 } }}>
                  <DateRangePicker
                    colorLabel={COLORS.white}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }}
              gap={{ xs: 1, md: 3 }}
            >
              <HighlightStat
                label="Top Product by Cost"
                name={data?.topProduct?.name}
                value={data?.topProduct ? formatHighlightCurrency(data.topProduct.total) : undefined}
                loading={loading}
                show
              />
              <HighlightStat
                label="Top Product by Volume"
                name={data?.topProductVolume?.name}
                value={
                  data?.topProductVolume
                    ? `${formatHighlightNumber(data.topProductVolume.total)} units`
                    : undefined
                }
                loading={loading}
                show
              />
              <HighlightStat
                label="Top Employee by Cost"
                name={data?.topUser?.name}
                value={data?.topUser ? formatHighlightCurrency(data.topUser.total) : undefined}
                loading={loading}
                show
              />
              <HighlightStat
                label="Top Employee by Volume"
                name={data?.topUserVolume?.name}
                value={
                  data?.topUserVolume
                    ? `${formatHighlightNumber(data.topUserVolume.total)} transactions`
                    : undefined
                }
                loading={loading}
                show
              />
            </Box>
          </Paper>
        </Box>

        <Box sx={{ pt: { xs: 1.5, md: 3 }, pb: 6 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            {dropZones.map((zone) => (
              <DropZoneCard key={zone.id} zone={zone} />
            ))}
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};